import { uniqBy, debounce, sortBy } from 'lodash';

import { usePromise } from '../utils/usePromise';
import { useInstance } from 'common-module/src/util/useInstance';
import { useGetter } from 'common-module/src/util/useVuex';
import { computed, ref, Ref, watch, nextTick } from '@vue/composition-api';
import { createEventHook, until } from '@vueuse/core';

import { getIssueLayout, getStickyTop, setStickyTop } from '../api/issue';
import { IssueAssembleResponse, IssueFieldInfo } from '../models/issue';
import { ActionStore, IssueDrawerActionEdit } from './action';
import { useFieldRenderScheduler } from './fieldRenderScheduler';
import { IssueTypeStore } from './issueType';
import { ProjectStore } from './project';
import { ShareStore } from './share';
import { PermissionStore } from './permission';

const hiddenFieldCodesOnVisitor = [
  'activities',
  'assignee',
  'activities',
  'comments',
  'follows',
  'owner',
  'createBy',
  'updateBy',
  'docs'
];

//不存在右侧field tab 的字段
const hiddenRightField = [
  'comments',
  'activities'
];

//在顶部固定的横向三个字段
const fixTopField = ['status', 'owner', 'assignee'];

const hiddenControlCodesOnVisitor = ['member'];

enum Area {
  Left = 'left area',
  Right = 'right area',
  Top = 'top area'
}

// 功能开关控制字段
const switchControlField: Record<string, any> = {
  // 'docs': 'doc_switch'
};

export const useLayout = (
  actionStore: ActionStore,
  projectStore: ProjectStore,
  issueTypeStore: IssueTypeStore,
  issueAssemble: Ref<IssueAssembleResponse | null>,
  requestRefreshOptions: () => void,
  shareStore: ShareStore,
  permissionStore: PermissionStore
) => {
  const fieldAreas = computed(() => issueAssemble.value?.issueAreaVOList || []);

  const instance = useInstance();

  const lazyTabs = ref<string[]>([]);

  const lastActiveRightTab = ref<'field' | 'comment' | 'plugin' | 'creatorInfo' | ''>('');
  //右侧头部tabs
  const activeRightTab = ref<'field' | 'comment' | 'plugin' | 'activities' | 'creatorInfo'>('field');

  const rightTabs = computed(() => {
    return [
      {
        key: 'field',
        label: instance.$t('issue.rightTab.field'),
        hide: false
      },
      {
        key: 'comment',
        label: instance.$t('issue.rightTab.comment'),
        hide: !showComment.value
      },
      {
        key: 'plugin',
        label: instance.$t('issue.rightTab.plugin'),
        hide: !showPlugin.value
      },
      {
        key: 'creatorInfo',
        label: 'Requester',
        hide: !showCreatorInfo.value
      }
    ];
  });


  const changeRightTab = (tab: 'field' | 'comment' | 'plugin' | 'activities' | 'creatorInfo') => {
    activeRightTab.value = tab;
    onChangeRightTabEvent.trigger(tab);
    if (lazyTabs.value.includes(tab)) {
      lazyTabs.value = lazyTabs.value.filter((t) => t !== tab);
    }
  };

  //激活评论框时调用
  const saveLastActiveRightTab = (tab: 'field' | 'comment' | 'plugin' | 'creatorInfo' | '') => {
    lastActiveRightTab.value = tab;
  };

  actionStore.onClose(async () => {
    await nextTick();
    activeRightTab.value = 'field';
    lastActiveRightTab.value = '';
    lazyTabs.value = [];
  });

  watch(actionStore.action, async (action) => {
    if (action && (action as IssueDrawerActionEdit)?.commentId) {
      await until(lazyRenderFields).toMatch(fields => !fields.includes('comments'));
      lazyTabs.value = [...lazyTabs.value, 'field'];
      changeRightTab('comment');
    }
  });

  const showComment = computed(() => {
    const id = actionStore.action.value?.type === 'Edit'
      ? actionStore.action.value.issueId
      : null;
    return actionStore.actionType.value === 'Edit' && !shareStore.isVisitor.value && id && permissionStore.hasPermission('create:comment') && !lazyRenderFields.value.includes('comments');
  });

  const showPlugin = computed(() => {
    const id = actionStore.action.value?.type === 'Edit'
      ? actionStore.action.value.issueId
      : null;
    return actionStore.actionType.value === 'Edit' && !shareStore.isVisitor.value && id && permissionStore.hasPermission('integration') && !lazyRenderFields.value.includes('plugin');
  });

  const showActivities = computed(() => {
    const id = actionStore.action.value?.type === 'Edit'
      ? actionStore.action.value.issueId
      : null;
    return actionStore.actionType.value === 'Edit' && !shareStore.isVisitor.value && id && !lazyRenderFields.value.includes('activities');
  });

  const showCreatorInfo = computed(() => {
    if (!issueAssemble.value?.requesterVO?.email) {
      return false;
    }
    const id = actionStore.action.value?.type === 'Edit'
      ? actionStore.action.value.issueId
      : null;
    return actionStore.actionType.value === 'Edit' && !shareStore.isVisitor.value && id && !lazyRenderFields.value.includes('creatorInfo');
  });
  const {
    result: stickyTopFieldCodesResponse,
    refresh: refreshStickyTop,
    isLoading: isLoadingStickyTop
  } = usePromise(
    async (project?: number, issueType?: number) =>
      project && issueType && !shareStore.isVisitor.value
        ? await getStickyTop(project, issueType)
        : [],
    [],
    { immediate: true }
  );

  const rightAreaStickyTopFieldCodes = computed(() =>
    stickyTopFieldCodesResponse.value
      .filter(({ type }) => type === 'top')
      .map(({ fieldCode }) => fieldCode)
  );

  const leftAreaStickyTopFieldCodes = computed(() =>
    stickyTopFieldCodesResponse.value
      .filter(({ type }) => type === 'tab_top')
      .map(({ fieldCode }) => fieldCode)
  );

  watch(
    [projectStore.currentProjectId, issueTypeStore.currentTypeId],
    ([project, type]) => {
      isLoadingStickyTop.value = true;
      refreshStickyTop(project, type);
    },
    { immediate: true }
  );

  const saveStickTopField = debounce(
    (
      codes: Array<{ fieldCode: string; type: 'top' | 'tab_top' }>,
      fields: IssueFieldInfo[],
      issueTypeId: number,
      projectId: number
    ) => {
      if (codes) {
        setStickyTop(
          projectId,
          issueTypeId,
          codes
            .map(
              ({ fieldCode, type }, index) =>
                ({
                  issueTypeId,
                  projectId,
                  fieldCode,
                  customFieldId:
                    fields.find((f) => f.fieldCode === fieldCode)
                      ?.customFieldId || 0,
                  ordinate: index,
                  areaName: type === 'top' ? 'right area' : 'left area',
                  type
                } as Parameters<typeof setStickyTop>[2][number])
            )
            .filter((item) => item.customFieldId)
        );
      }
    },
    2000
  );

  const stickTopField = (code: string, type: 'top' | 'tab_top') => {
    stickyTopFieldCodesResponse.value = uniqBy(
      [
        {
          type,
          fieldCode: code
        },
        ...stickyTopFieldCodesResponse.value
      ],
      (f) => f.fieldCode
    );
    instance.$track.send('Issue_FieldLabel', { type: code }, true);

    saveStickTopField(
      stickyTopFieldCodesResponse.value,
      allFields.value,
      issueTypeStore.currentTypeId.value!,
      projectStore.currentProjectId.value!
    );
  };

  const cancelStickTopField = (code: string) => {
    stickyTopFieldCodesResponse.value =
      stickyTopFieldCodesResponse.value.filter((c) => c.fieldCode !== code);
    saveStickTopField(
      stickyTopFieldCodesResponse.value,
      allFields.value,
      issueTypeStore.currentTypeId.value!,
      projectStore.currentProjectId.value!
    );
  };

  const isLoadingLayout = ref(false);
  const reloadLayout = async () => {
    if (
      !isLoadingLayout.value &&
      issueAssemble.value &&
      projectStore.currentProjectId.value &&
      issueTypeStore.currentTypeId.value
    ) {
      try {
        isLoadingLayout.value = true;
        requestRefreshOptions();
        const res = await getIssueLayout(
          projectStore.currentProjectId.value,
          issueTypeStore.currentTypeId.value
        );
        refreshStickyTop(
          projectStore.currentProjectId.value,
          issueTypeStore.currentTypeId.value
        );

        issueAssemble.value.issueAreaVOList = res;
      } finally {
        isLoadingLayout.value = false;
      }
    }
  };

  const filedAreaMap = computed(() => {
    return fieldAreas.value.reduce((acc: any, area) => {
      acc[area.areaName] = area.groupList[0].issueFieldOrdinateVOList.flatMap((fieldVO: any) => {
        return fieldVO.issueFieldInfoVo.filter((field: any) => {
          return field.showed === 1 && !isHiddenField(field) && isOpenSwitch(field.fieldCode);
        });
      }).sort((a: IssueFieldInfo, b: IssueFieldInfo) => a.ordinate - b.ordinate);
      return acc;
    }, {});
  });

  const leftAreaFields = computed(() => filedAreaMap.value[Area.Left] || []);

  const rightAreaFields = computed(() => {
    const fields = (filedAreaMap.value[Area.Right] || []).filter((item: IssueFieldInfo) => {
      return !hiddenRightField.includes(item.fieldCode);
    });
    // 处理置顶的数据
    if (!rightAreaStickyTopFieldCodes.value.length) {
      return fields;
    }
    return sortBy(fields, (a: IssueFieldInfo) => {
      const index = rightAreaStickyTopFieldCodes.value.indexOf(a.fieldCode);
      return index === -1 ? 1000 : index;
    });
  });

  const topAreaFields = computed(() => filedAreaMap.value[Area.Top] || []);

  const isHiddenField = (field: IssueFieldInfo) => {
    return shareStore.isVisitor.value ? (
      hiddenControlCodesOnVisitor.includes(field.controlCode) ||
      hiddenFieldCodesOnVisitor.includes(field.fieldCode)
    ) : false;
  };

  const isOpenSwitch = (fieldCode: string) => {
    return switchControlField[fieldCode] ? useGetter('features/getFeature')?.(switchControlField[fieldCode]) : true;
  };

  const topAreaFieldCodes = computed(() => topAreaFields.value.map((f: IssueFieldInfo) => f.fieldCode));
  const leftAreaFieldCodes = computed(() => leftAreaFields.value.map((f: IssueFieldInfo) => f.fieldCode));
  const rightAreaFieldCodes = computed(() => rightAreaFields.value.map((f: IssueFieldInfo) => f.fieldCode));

  const allFields = computed(() => [
    ...topAreaFields.value,
    ...leftAreaFields.value,
    ...rightAreaFields.value
  ]);

  const allRequiredFieldCodes = computed(() => {
    return allFields.value
      .filter((field: IssueFieldInfo) => field.requiredFlag === 1)
      .map((field: IssueFieldInfo) => field.fieldCode);
  });

  const allFieldCodes = computed(() => {
    return allFields.value.map((field: IssueFieldInfo) => field.fieldCode);
  });

  const showFieldLayout = async () => {
    if (
      projectStore.currentProject.value &&
      issueTypeStore.currentIssueType.value &&
      issueAssemble.value
    ) {
      instance.eventBus.$emit('config-field-layout:show', {
        issueType: issueTypeStore.currentIssueType.value,
        projectId: projectStore.currentProject.value!.id,
        projectName: projectStore.currentProject.value!.pname,
        projectColor: projectStore.currentProject.value!.backgroundColor
      });
      instance.$track.send('Issue_Adjustment', {}, true);
    }
  };


  const { restart, lazyRenderFields } = useFieldRenderScheduler();

  const fieldComponentMountedEvent = createEventHook<string>();

  const onClickCommentInputEvent = createEventHook<string>();

  const onChangeRightTabEvent = createEventHook<string>();

  return {
    topAreaFieldCodes,
    leftAreaFieldCodes,
    rightAreaFieldCodes,
    isLoadingLayout,
    reloadLayout,
    showFieldLayout,
    activeRightTab,
    rightTabs,
    changeRightTab,
    lastActiveRightTab,
    saveLastActiveRightTab,
    showComment,
    showPlugin,
    showActivities,
    showCreatorInfo,
    fieldAreas,
    lazyTabs,
    topAreaFields,
    leftAreaFields,
    rightAreaFields,
    rightAreaStickyTopFieldCodes,
    allFields,
    allFieldCodes,
    allRequiredFieldCodes,
    stickTopField,
    leftAreaStickyTopFieldCodes,
    cancelStickTopField,
    isLoadingStickyTop,
    lazyRenderFields,
    fixTopField,
    startRenderLazyFields: restart,
    fireFieldComponentMounted: fieldComponentMountedEvent.trigger,
    onFieldComponentMounted: fieldComponentMountedEvent.on,
    onClickCommentInputEventTrigger: onClickCommentInputEvent.trigger,
    onClickCommentInputEventOn: onClickCommentInputEvent.on,
    onChangeRightTabEventTrigger: onChangeRightTabEvent.trigger,
    onChangeRightTabEventOn: onChangeRightTabEvent.on,
    filedAreaMap
  };
};

export type LayoutStore = ReturnType<typeof useLayout>;
